import * as React from "react";
import { Text } from "@uikit";

import { useStaticQuery, graphql, Link } from "gatsby";
import { Coordinate } from "@kalabo/golfsavers-common";
import {
  calcCrow,
  getCourseDaysOfOperation,
  slugify,
} from "@kalabo/golfsavers-common";
import { isRegionalSite } from "@utilities/core";
import {
  Course,
  CourseExtended,
  CourseReview,
} from "@kalabo/golfsavers-common";
import StarRating from "@components/starRating";
import { FaMapMarkerAlt } from "@react-icons/all-files/fa/FaMapMarkerAlt";
import { Region } from "@kalabo/golfsavers-common";

interface iCoursesProps {
  region: Region;
  courses: Course[];
  center: Coordinate;
  maxDistance?: number;
  listMode?: boolean;
  mode?: "large" | "small";
  fromArea?: string;
  limit?: number;
}

const NearbyCourses = ({
  region,
  courses,
  center,
  maxDistance = 10,
  listMode = false,
  fromArea = "",
  mode = "small",
  limit = 3,
}: iCoursesProps) => {
  const nearby_courses = courses
    .map((course: Course) => {
      return {
        ...course,
        distance: calcCrow(
          center.latitude,
          center.longitude,
          course.latitude,
          course.longitude
        ),
      } as CourseExtended;
    })
    .sort((a: CourseExtended, b: CourseExtended) => {
      return a.distance.km - b.distance.km;
    })
    .filter((course: CourseExtended) => {
      return (
        course.latitude !== center.latitude &&
        course.longitude !== center.longitude
      );
    })
    .filter((course: CourseExtended) => {
      return course.distance.km < maxDistance;
    })
    .slice(0, limit);

  const getPriceRange = (price_range: string) => {
    switch (price_range?.toLowerCase()) {
      case "premium":
        return "$$$$";
      case "expensive":
        return "$$$";
      case "moderate":
        return "$$";
      case "budget":
        return "$";
      default:
        return "";
    }
  };

  if (nearby_courses.length === 0) return null;

  const courseList = () => {
    return (
      <>
        {nearby_courses.map((course: CourseExtended, idx: any) => {
          let reviews: CourseReview[] = course.reviews?.reviews || [];
          let rating = course?.reviews?.average_rating || 0;
          let priceRange = getPriceRange(course.price_range);
          let days_of_operation = getCourseDaysOfOperation(
            course.days_of_operation
          );

          let featured_image = course.featured_image;
          return (
            <Link
              to={
                isRegionalSite()
                  ? `/${region.slug}-golf-courses/${course.slug}`
                  : `/${slugify(region.country.slug)}/${slugify(
                      region.name.en.toLowerCase()
                    )}-golf-courses/${course.slug}`
              }
              key={idx}
              className="group  flex gap-[10px] items-center"
            >
              <div
                className={`${
                  mode === "large"
                    ? "min-w-[115px] lg:min-w-[150px] "
                    : "min-w-[70px]"
                } rounded-[4px] overflow-hidden bg-black `}
              >
                <img
                  className={`object-center object-cover ${
                    mode === "large"
                      ? "w-[115px] h-[115px] lg:w-[150px] "
                      : "w-[70px]"
                  } transition-all group-hover:opacity-90`}
                  alt={featured_image?.alt || course.name.en}
                  src={`${process.env.GATSBY_IMAGE_PREFIX}${
                    featured_image?.url
                  }?${
                    mode === "large"
                      ? "aspect_ratio=6:4&width=150&height=120"
                      : "aspect_ratio=1:1&width=70&height=70"
                  })`}
                />
              </div>

              <div className="flex flex-col gap-1 overflow-hidden">
                <Text
                  element="h3"
                  size={15}
                  color="black90"
                  weight="semibold"
                  leading={1.3}
                  className="group-hover:text-blue group-hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                >
                  {" "}
                  {course.name.en}
                </Text>

                {mode === "large" ? (
                  <>
                    {" "}
                    <div className="flex items-center gap-1">
                      <StarRating mode={"small"} rating={rating} size="small" />
                      <div className="hidden sm:block bg-grey3 h-[18px] w-[1px] border-grey2"></div>
                      <Text
                        sizeDesktop={13}
                        sizeTablet={13}
                        size={13}
                        className="block"
                        color="black80"
                      >
                        <div className="flex gap-1 items-center">
                          <FaMapMarkerAlt className="w-3 h-3 text-grey6" />
                          <span>
                            {course.distance.km.toFixed(1)} km from {fromArea}
                          </span>
                        </div>
                      </Text>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <StarRating
                        mode="large"
                        hideCount
                        rating={rating}
                        size="small"
                      />

                      <Text size={13} color="black60" leading={1.3}>
                        ({reviews.length})
                      </Text>
                    </div>
                    <div className="flex items-center">
                      <Text
                        size={13}
                        color="black80"
                        leading={1.3}
                        className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                      >
                        {course.distance.km.toFixed(1)} km · {priceRange} ·{" "}
                        {days_of_operation}
                      </Text>
                    </div>
                  </>
                )}
                {mode === "large" && (
                  <div className="flex items-center">
                    <Text
                      sizeTablet={13}
                      size={13}
                      italic
                      leading={1.5}
                      color="black90"
                    >
                      <q>{course.summary?.en}</q>
                    </Text>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </>
    );
  };

  if (listMode) {
    return courseList();
  }
  return (
    <div className="mb-10 flex-col flex flex-1 overflow-hidden">
      <Text
        size={17}
        element="h3"
        weight="semibold"
        color="black90"
        leading={1.3}
        className="mb-4"
      >
        Courses Nearby
      </Text>
      <div className="flex flex-col gap-2">{courseList()}</div>
    </div>
  );
};

export default NearbyCourses;
